import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, memo, useContext, useEffect, useState } from 'react';
import { Divider, Flex, Button, Center, Text, Menu, MenuButton, MenuItem, MenuList, Grid, VStack } from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { useT } from '@progress-fe/core';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldInspector } from '../../jsFormBase';
import { CompressorCurve } from './components';
const checkCurveValidity = (curve, existedCurves) => {
    const checkName = curve.name.toLowerCase();
    const isNotUniqName = existedCurves.some((c) => c.id !== curve.id && c.name.toLowerCase() === checkName);
    if (isNotUniqName) {
        return {
            isNameInvalid: true
        };
    }
    return null;
};
/**
 * Component renders "/schemas/jsf-compressor-curves" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TCompressorCurveFormData.
 */
const CompressorCurvesJsFieldFC = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const [curvesErrors, setCurvesErrors] = useState({});
    const [validCurves, setValidCurves] = useState([]);
    const { t } = useT();
    const fieldValues = props.formData?.fieldValues ?? [];
    const fieldConfig = props.formData?.fieldConfig ?? {
        flowTypeOptions: [],
        pressureTypeOptions: []
    };
    useEffect(() => {
        if (!props.formData?.fieldValues) {
            return;
        }
        const allErrors = props.formData.fieldValues.reduce((acc, current) => {
            const errors = checkCurveValidity(current, props.formData.fieldValues);
            if (errors) {
                acc[current.id] = errors;
            }
            return acc;
        }, {});
        setCurvesErrors(allErrors);
        const valid = props.formData.fieldValues.filter((item) => !!item.name && !allErrors[item.id]);
        setValidCurves(valid);
    }, [props.formData?.fieldValues]);
    const handleAddCurve = () => {
        props.onChange({
            ...props.formData,
            fieldValues: [
                ...fieldValues,
                {
                    id: uuidv4(),
                    name: '',
                    values: [
                        {
                            efficiency: '',
                            flow: '',
                            pressure: ''
                        }
                    ]
                }
            ]
        });
    };
    const handleUpdateCurve = (updatedCurve) => {
        const errors = checkCurveValidity(updatedCurve, fieldValues);
        if (errors) {
            setCurvesErrors((map) => ({ ...map, [updatedCurve.id]: errors }));
            return;
        }
        if (curvesErrors[updatedCurve.id]) {
            setCurvesErrors((map) => {
                const newMap = { ...map };
                delete newMap[updatedCurve.id];
                return newMap;
            });
        }
        const newCurves = fieldValues.map((c) => (c.id === updatedCurve.id ? { ...updatedCurve } : c));
        props.onChange({
            ...props.formData,
            fieldValues: newCurves
        });
    };
    const handleDeleteCurve = (curve) => {
        const newCurves = fieldValues.filter((c) => c.id !== curve.id);
        props.onChange({
            ...props.formData,
            fieldValues: newCurves
        });
    };
    const handleCopyCurve = (baseCurve) => {
        const newCurves = [...fieldValues];
        newCurves.push({ ...baseCurve, id: uuidv4(), name: `Копия ${baseCurve.name}` });
        props.onChange({
            ...props.formData,
            fieldValues: newCurves
        });
    };
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Flex, { pb: '2px', "data-testid": "CompressorCurveJsField-test", "data-name": props.name, flexDirection: "column", gap: "8px", children: [fieldValues.length === 0 && (_jsx(Center, { h: "70px", children: _jsxs(Text, { children: [" ", t('jsfields.CompressorCurve.noCurves')] }) })), !!fieldValues.length && (_jsx(VStack, { overflowY: "auto", maxH: "500px", gap: "8px", children: fieldValues.map((curve, idx) => {
                        return (_jsxs(Fragment, { children: [_jsx(CompressorCurve, { isReadonly: !!props.schema.readOnly, fieldConfig: fieldConfig, curve: curve, errors: curvesErrors[curve.id], onChange: (updatedCurve) => {
                                        handleUpdateCurve(updatedCurve);
                                    }, onDelete: () => {
                                        handleDeleteCurve(curve);
                                    } }), idx !== fieldValues.length - 1 && _jsx(Divider, {})] }, curve.id));
                    }) })), !props.schema.readOnly && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Grid, { gap: "8px", w: "100%", gridTemplateColumns: "1fr 1fr", children: [_jsx(Button, { variant: "ghost", size: "smSquare", w: "100%", onClick: handleAddCurve, children: t('jsfields.CompressorCurve.addCurve') }), _jsxs(Menu, { matchWidth: true, offset: [0, 2], variant: "outline", children: [_jsx(MenuButton, { size: "xs", as: Button, width: "100%", variant: "ghost", isDisabled: !validCurves.length, children: t('jsfields.CompressorCurve.copyCurve') }), _jsx(MenuList, { motionProps: { animate: false }, zIndex: 2, children: validCurves.map((curve) => (_jsx(MenuItem, { onClick: () => handleCopyCurve(curve), children: curve.name }, curve.id))) })] })] })] }))] }) }));
};
export const CompressorCurveJsField = memo(CompressorCurvesJsFieldFC);
//# sourceMappingURL=CompressorCurvesJsField.js.map